import {useIntl} from 'react-intl';
import * as React from 'react';
import {useEffect, useRef} from 'react';
import {SimpleDataTable} from "react-ive-tables";
import {Button} from "primereact/button";
import {Calendar} from "primereact/calendar";
import moment from "moment";
import {PaymentService} from "../../lib/services/payment-service";
import {useToast} from "../../lib/hooks/useToast";
import {mutate} from "swr";
import {useDialog} from "../../lib/hooks/useDialog";
import {InputNumber} from "primereact/inputnumber";
import {ColumnBodyOptions} from "primereact/column";
import {DataTableRowEditCompleteEvent} from "primereact/datatable";
import {Payment, PaymentResponse} from "../../lib/types/types";

interface Props {
    data: Payment[] | undefined;
    paymentAdded?: number;
    previewOnly: boolean;
}

export const Payments: React.FC<Props> = props => {
    const {formatMessage: f} = useIntl();
    const didMountRef = useRef(false);
    const toastHelpers = useToast();
    const paymentService = new PaymentService(f, toastHelpers);
    const {showConfirmDialog} = useDialog();

    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
        }
    }, []);

    useEffect(() => {
        if (props.paymentAdded) {
            setTimeout(() => {
                const element = document.querySelector("#edit-payment-button-0");
                //@ts-ignore
                if (element) element.click();
            }, 100)

        }
    }, [props.paymentAdded])

    const getColumnTemplate = () => {
        return {
            paymentDate: (rowData: PaymentResponse) => moment(rowData.paymentDate).format("DD-MM-YYYY"),
            operations: (rowData: PaymentResponse, columnOptions: ColumnBodyOptions) => {
                const shouldRenderButton = rowData.bankTransactionId === undefined || rowData.bankTransactionId === null;

                const editButton = columnOptions.rowEditor && !columnOptions.rowEditor.editing ?
                    <Button id={`edit-payment-button-${rowData.id}`} icon={'pi pi-pencil'} tooltip={f({id: 'edit'})}
                            tooltipOptions={{position: 'top'}} onClick={columnOptions.rowEditor.onInitClick}/> : null;
                const saveButton = columnOptions.rowEditor && columnOptions.rowEditor.editing ?
                    <Button icon={'pi pi-check'} className={"p-button-success"} tooltip={f({id: 'save'})} tooltipOptions={{position: 'top'}}
                            onClick={columnOptions.rowEditor.onSaveClick}/> : null;
                const cancelButton = columnOptions.rowEditor && columnOptions.rowEditor.editing ?
                    <Button className={'ml-2'} icon={'pi pi-times'} tooltip={f({id: 'cancel'})}
                            tooltipOptions={{position: 'top'}} onClick={columnOptions.rowEditor.onCancelClick}/> : null;
                return <>
                    {shouldRenderButton && editButton}
                    {saveButton}
                    {cancelButton}
                    {<Button icon={'pi pi-trash'} className={'p-button-danger ml-2'} tooltip={f({id: 'delete'})}
                             tooltipOptions={{position: 'top'}} onClick={() => handleDelete(rowData)}/>}
                </>
            },
            amount: (rowData: Payment) => rowData.amount + ` ${f({id: "bgn"})}.`
        }
    }

    const getSpecialEditors = () => {
        return {
            paymentDate: (options: any) => <Calendar value={options.value} dateFormat={"dd-mm-yy"}
                                                     onChange={(e) => options.editorCallback(e.value)}/>,
            amount: (options: any) => <InputNumber value={options.value} useGrouping={false} mode="decimal"
                                                   maxFractionDigits={2}
                                                   onChange={(e) => options.editorCallback(e.value)}/>
        }
    }

    const handleCreate = async (data: Payment) => {
        const newPayment = await paymentService.create(data);
        if (newPayment !== null) {
            await mutate('/invoices/lazy');
            await mutate('/dashboard/overdueInvoices')
            await mutate('/clients/invoices-dropdown');
        }
    }

    const handleUpdate = async (data: Payment) => {
        const updatedPayment = await paymentService.update(data);
        if (updatedPayment !== null) {
            await mutate('/invoices/lazy')
            await mutate('/clients/invoices-dropdown');
        }
    }

    const handleDelete = async (payment: Payment) => {
        if (payment.id !== undefined) {
            showConfirmDialog({
                body: f({id: 'confirmDeletePayment'}, {
                    paymentDate: moment(payment.paymentDate).format("DD-MM-YYYY"),
                    paymentAmount: payment.amount
                })
            }).then((confirmed) => {
                if (!confirmed) return;
                paymentService.delete(payment.id!).then(async res => {
                    if (res) {
                        await mutate('/invoices/lazy');
                        await mutate('/clients/invoices-dropdown');
                    }
                })
            })
        }
    }

    const handleRowEdit = async (event: DataTableRowEditCompleteEvent) => {
        const newValues: Payment = event.newData as Payment;
        //Update
        if (newValues.id !== 0) {
            await handleUpdate(newValues);
        }
        //Create
        else {
            await handleCreate(newValues);
        }
    }

    return <div className={"flex flex-column col-12"}>
        <h3 style={{textAlign: 'center'}}>{f({id: 'payments'})}</h3>
        <SimpleDataTable data={props.data} showHeader={false}
                         columnOrder={props.previewOnly ? ['paymentDate', 'amount'] : ['paymentDate', 'amount', 'operations']}
                         specialEditors={props.previewOnly ? undefined : getSpecialEditors()}
                         columnTemplate={getColumnTemplate()}
                         ignoreFilters={['operations']}
                         showFilters={false}
                         editableColumns={['paymentDate', 'amount']}
                         rowEditHandler={props.previewOnly ? undefined : handleRowEdit}
                         showPaginator={false}
                         specialLabels={{amount: 'vatIncluded'}}/>
    </div>
};
